import API from '../States/API';
import Books from '../Components/Read/Books.svelte';
import Chapters from '../Components/Read/Chapters.svelte';
import Chapter from '../Components/Read/Chapter.svelte';
import Data from '../States/Data';
import Empty from '../Components/Empty.svelte';
import Favorite from '../Components/Favorite.svelte';
import Plans from '../Components/Plans/Plans.svelte';
import Plan from '../Components/Plans/Plan.svelte';
import HelpBB from '../Components/Help/BB.svelte';
import HelpVB from '../Components/Help/VB.svelte';
import Helper from '../Models/Helper';
import Index from '../Components/Read/Index.svelte';
import InfoBB from '../Components/Info/BB.svelte';
import InfoVB from '../Components/Info/VB.svelte';
import Missing from '../Components/Missing.svelte';
import PrivacyBB from '../Components/Privacy/BB.svelte';
import PrivacyVB from '../Components/Privacy/VB.svelte';
import Profile from '../Components/Account/Profile.svelte';
import Router from '../States/Router';
import Scripture from '../Models/Scripture';
import Search from '../Components/Search.svelte';
import Session from '../States/Session';
import SignIn from '../Components/Account/SignIn.svelte';
import SignUp from '../Components/Account/SignUp.svelte';
import Slides from '../Components/Layout/Slides.svelte';
import Start from '../Components/Start.svelte';
import Verify from '../Components/Account/Verify.svelte';
import VerifyPassword from '../Components/Account/VerifyPassword.svelte';

import {get, writable} from 'svelte/store';

const slides = {
	active: {component: Empty, properties: {type: 'empty', id: 0}, slide: 0, instant: false},
	slide_1: {component: Empty, properties: {type: 'empty', id: 0}, events: {}},
	slide_2: {component: Empty, properties: {type: 'empty', id: 0}, events: {}},
	slide_3: {component: Empty, properties: {type: 'empty', id: 0}, events: {}},
	slide_4: {component: Empty, properties: {type: 'empty', id: 0}, events: {}},
	slide_5: {component: Empty, properties: {type: 'empty', id: 0}, events: {}},
	slide_6: {component: Empty, properties: {type: 'empty', id: 0}, events: {}},
	slide_7: {component: Empty, properties: {type: 'empty', id: 0}, events: {}}
};

const slider = writable(slides);

/*
 * A slide can hold swipe navigation while something is running that a stray
 * gesture would ruin — chapter audio does exactly that. Keyed on the slide
 * index, because slides 4, 5 and 6 are all Chapter instances and only one of
 * them is sounding; a single shared flag would let the silent ones release the
 * hold of the one that plays. The value is the notice to show when a swipe is
 * refused.
 */
const holds = {};

slider.hold = (index, notice) => {
	if (notice) holds[index] = notice;
	else delete holds[index];
}

slider.holding = () => {
	const keys = Object.keys(holds);
	return keys.length ? holds[keys[0]] : null;
}

slider.subscribe(state => {
	Helper.title();
});

slider.check = (index, type, properties) => {
	const slide = slides[`slide_${index}`];

	let valid = false;
	if (Array.isArray(type)) valid = type.includes(slide.properties.type);
	else valid = slide.properties.type.toLowerCase() === type.toLowerCase();

	valid = valid && (!properties.id || (slide.properties.id === properties.id));
	return valid;
}

slider.properties = index => {
	return slides[`slide_${index}`].properties;
}

slider.active = (slide, state, silent) => {
	if (Helper.undefined(state)) state = true;

	const id = slides.active.slide;
	if (id) slides[`slide_${id}`].properties.active = false;

	let current = slides[`slide_${slide}`];
	const empty = current.properties.type === 'empty';
	if (empty) current = slides['slide_1'];
	const properties = current.properties;
	properties.active = state;

	const instant = true;
	const component = current.component;
	slides.active = {component, properties, slide, instant};

	if (!silent)
		slider.set(slides);
}

slider.previous = () => {
	const id = slides.active.slide;
	if (id) for (let index = id - 1; index > 0; index--) {
		const slide = slides[`slide_${index}`];
		const change = slide.properties.type != 'empty';
		if (change) {
			slider.active(index);
			if (Helper.type(slide.events.onPrevious, 'function')) {
				slide.properties.event = 'previous';
				slide.events.onPrevious(slide.properties);
			}

			break;
		}
	}
	else slider.missing();
}

slider.next = () => {
	const id = slides.active.slide;
	if (id) for (let index = id + 1; index < 7; index++) {
		const slide = slides[`slide_${index}`];
		const change = slide.properties.type != 'empty';
		if (change) {
			slider.active(index);
			if (Helper.type(slide.events.onNext, 'function')) {
				slide.properties.event = 'next';
				slide.events.onNext(slide.properties);
			}

			break;
		}
	}
	else slider.missing();
}

slider.close = () => {
	const id = slides.active.slide;
	if (id) {

		const slide = slides[`slide_${id}`];
		slide.properties.event = 'close';

		if (Helper.type(slide.events.onClose, 'function'))
			slide.events.onClose(slide.properties);
		else
			slider.read(slide.properties);
	}
}

slider.start = () => {
	assign(1, Start, {type: 'start', active: true});
	slider.set(slides);
}

slider.read = properties => {
	/* Properties will be passed by the onClose event. */
	const reading = slides.slide_5.properties.type === 'chapter';
	if (reading) {
		slider.active(5);
		reset(1, 3);
		reset(7);
	}
	else slider.chapter('GEN_1');
}

slider.testament = code => {
	const testament = code ? code.toLowerCase() : 'ot';
	assign(1, Books, {type: 'books', testament: 'OT', active: testament == 'ot'});
	assign(2, Books, {type: 'books', testament: 'NT', active: testament == 'nt'});
	reset(3);
	slider.set(slides);
}

slider.chapters = (book, chapter) => {
	assign(1, Books, {type: 'books', testament: 'OT'});
	assign(2, Books, {type: 'books', testament: 'NT'});
	assign(3, Chapters, {type: 'chapters', book, chapter, active: true});
	slider.set(slides);
}

slider.chapter = async (location, options) => {
	const align = properties => {
		const slide = () => {
			const next = properties.event === 'next';
			const options = {old_active: next ? 4 : 6}
			slides.active.instant = true;
			slider.chapter(properties.chapter, options);

			const reset = () => {
				slides.active.instant = false;
				slider.set(slides);
			};

			reset();
		};

		slide();
	};

	if (!location) location = 'GEN_1';
	if (!Helper.is(options)) options = {};

	const pair = location.split('_');
	const book = pair[0];
	const chapter = parseInt(pair[1]);
	const previous = await Scripture.previousChapter(location);
	const next = await Scripture.nextChapter(location);
	const events = {onNext: align, onPrevious: align}

	/*
	 * A reading can run over several chapters (Genesis 1 - 3), so every chapter it
	 * covers shows the plan. Which reading that is comes from the session rather
	 * than from the slide being replaced, so it survives a trip outside the
	 * passage: click a chapter too far and back, and the bar is there again.
	 *
	 * The neighbours are given it as well as the middle slide. They are built
	 * before the swipe starts, so the bar is in place from the first frame instead
	 * of appearing once align() has rebuilt everything.
	 */
	const carry = (properties, code) => {
		const reading = get(Session).reading;
		if (reading && Scripture.passageCovers(reading.passage, code)) {
			properties.plan_code = reading.plan;
			properties.passage_code = reading.passage;
		}

		/* Which verses light up differs per chapter: Matteüs 10:16 - 11:1 runs
		 * from verse 16 to the end of the one and covers only verse 1 of the next. */
		const reference = properties.plan_code ? Scripture.passageReference(properties.passage_code, code) : null;
		if (reference) properties.reference = reference;

		return properties;
	};

	const properties_4 = {type: 'chapter', chapter: previous.code, pericope: ''};
	properties_4.old_active = options.old_active === 4;
	assign(4, Chapter, carry(properties_4, previous.code), events);

	/* The chapter that was asked for keeps the plan whatever the passage code
	   turns out to cover — that is where the reader arrives. */
	const properties_5 = {type: 'chapter', chapter: location, pericope: options.pericope, active: true};
	if (options.plan_code) properties_5.plan_code = options.plan_code;
	if (options.passage_code) properties_5.passage_code = options.passage_code;
	assign(5, Chapter, carry(properties_5, location), events);

	const properties_6 = {type: 'chapter', chapter: next.code, pericope: ''};
	properties_6.old_active = options.old_active === 6;
	assign(6, Chapter, carry(properties_6, next.code), events);

	reset(1, 3);
	reset(7);
	slider.set(slides);
}

/*
 * Opens a reading plan passage. A passage can be part of a chapter, so the
 * chapter that is opened comes from the passage code. This is the one place a
 * reader picks up a reading, so it is where the session is told about it; from
 * there carry() above puts the plan on every chapter the reading covers, and
 * marks the verses that belong to each of them.
 */
slider.passage = (passage_code, plan_code) => {
	const passage = Scripture.parsePassage(passage_code);
	Session.updateReading(plan_code, passage_code);

	if (!passage) return slider.chapter(passage_code, {plan_code});

	return slider.chapter(passage.location, {plan_code, passage_code});
}

slider.reference = (reference, target, source) => {
	if (!target) target = 7;
	if (!source) source = 5;

	const onClose = () => {
		slider.active(source);
		reset(target);
	};

	const chapter = `${reference.book}_${reference.chapter}`;
	const properties = {type: 'reference', chapter, reference, active: true}
	assign(target, Chapter, properties, {onClose});
	slider.set(slides);
}

slider.index = () => {
	assign(1, Index, {type: 'index', active: true});
	reset(2, 3);
	slider.set(slides);
}


slider.search = query => {
	assign(1, Search, {type: 'search', active: true, query});
	reset(2, 3);
	reset(7);
	slider.set(slides);
}

slider.favorite = () => {
	assign(1, Favorite, {type: 'favorite', active: true});
	reset(2, 3);
	reset(7);
	slider.set(slides);
}

slider.plans = () => {
	assign(1, Plans, {type: 'plans', active: true});
	reset(2, 3);
	reset(7);
	slider.set(slides);
}

/*
 * Zonder dag opent het plan waar de lezer is (de eerste onafgemaakte dag, zie
 * Plan.svelte). Met dag opent het daar: de balk boven een hoofdstuk stuurt je
 * naar een dag met naam, dus die belofte mag niet door "waar was ik" worden
 * overruled.
 */
slider.plan = (code, day) => {
	assign(1, Plans, {type: 'plans', active: false});
	assign(2, Plan, {type: 'plan', active: true, code, day});
	reset(3);
	slider.set(slides);
}

slider.help = () => {
	const code = get(Data).version.code;
	let handler; switch (code) {
		case 'VB': handler = HelpVB; break;
		default: handler = HelpBB; break;
	}

	assign(1, handler, {type: 'help', active: true});
	reset(2, 3);
	slider.set(slides);
}

slider.info = () => {
	const code = get(Data).version.code;
	let handler; switch (code) {
		case 'VB': handler = InfoVB; break;
		default: handler = InfoBB; break;
	}

	assign(1, handler, {type: 'info', active: true});
	reset(2, 3);
	slider.set(slides);
}

slider.privacy = () => {
	const code = get(Data).version.code;
	let handler; switch (code) {
		case 'VB': handler = PrivacyVB; break;
		default: handler = PrivacyBB; break;
	}

	assign(1, handler, {type: 'privacy', active: true});
	reset(2, 3);
	slider.set(slides);
}

slider.missing = () => {
	assign(1, Missing, {type: 'missing', active: true});
	reset(2, 3);
	slider.set(slides);
}

/* Account */

slider.signUp = () => {
	assign(1, SignUp, {type: 'sign_up', active: true});
	reset(2, 3);
	slider.set(slides);
}

slider.signIn = () => {
	assign(1, SignIn, {type: 'sign_in', active: true});
	reset(2, 3);
	slider.set(slides);
}

slider.signOut = () => {
	API.signOut();
}

slider.verify = code => {
	assign(1, Verify, {type: 'verify', active: true, code});
	reset(2, 3);
	slider.set(slides);
}

slider.verifyPassword = code => {
	assign(1, VerifyPassword, {type: 'verify_password', active: true, code});
	reset(2, 3);
	slider.set(slides);
}

slider.profile = () => {
	assign(1, Profile, {type: 'profile', active: true});
	reset(2, 3);
	slider.set(slides);
}

/* Shared */

slider.job = (type, data) => {
	if (Helper.type(slider[type], 'function')) {
		const config = Array.isArray(data) ? data : [];
		slider[type](...config);
	}
}

slider.history = (type, data) => {
	slider.job(type, data);
}

slider.route = (current, history) => {
	if (Helper.type(current, 'string')) current = [current, []];
	if (Helper.type(history, 'string')) history = [history, []];

	if (!history) history = current;
	if (!current[1]) current[1] = [];
	if (!history[1]) history[1] = [];

	const uri = `/state/${history[0]}`;
	const state = {uri, data: history[1], session: false};
	Router.push(state);

	slider.job(current[0], current[1]);
}

/* Private */

const reset = (from, to) => {
	let start = from;
	let stop = to ? to : start;
	for (let id = start; id <= stop; id++) {
		slides[`slide_${id}`].component = Empty;
		slides[`slide_${id}`].properties = {type: 'empty', id: 0, index: 0, active: false};
		slides[`slide_${id}`].events = {};
	}
}

const assign = (slide, component, properties, events) => {
	const previous = slides[`slide_${slide}`];
	const previous_id = previous.properties.id;
	const change = previous.component != component;

	slides[`slide_${slide}`].component = component;
	slides[`slide_${slide}`].properties = properties;
	slides[`slide_${slide}`].properties.id = change ? Math.random() : previous_id;
	slides[`slide_${slide}`].events = Helper.undefined(events) ? {} : events;
	if (Helper.is(properties.active))
		slider.active(slide, true, true);
}

export default slider;